import { Fragment, useEffect, useMemo, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, BuildingOffice2Icon, ChartPieIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'

const initSelections = (obras) =>
    Object.fromEntries(obras.map((o) => [o.id, { selected: obras.length === 1, porcentagem: obras.length === 1 ? '' : '' }]))

const ModalVincularObrasInvestidor = ({ open, onClose, investidorNome, obras = [], loading = false, onProcessQueue }) => {
    const [selections, setSelections] = useState({})
    const [error, setError] = useState(null)
    const [processing, setProcessing] = useState(false)
    const [queueProgress, setQueueProgress] = useState({ done: 0, total: 0, current: '' })
    const [results, setResults] = useState([])

    const multiplasObras = obras.length > 1

    useEffect(() => {
        if (open && obras.length && !loading) {
            setSelections(initSelections(obras))
            setError(null)
            setProcessing(false)
            setQueueProgress({ done: 0, total: 0, current: '' })
            setResults([])
        }
    }, [open, obras, loading])

    const selecionadas = useMemo(
        () => obras.filter((o) => selections[o.id]?.selected),
        [obras, selections]
    )

    const totalPorcentagem = useMemo(
        () => selecionadas.reduce((sum, o) => sum + (parseFloat(selections[o.id]?.porcentagem) || 0), 0),
        [selecionadas, selections]
    )

    const toggleObra = (obraId) => {
        setSelections((prev) => ({
            ...prev,
            [obraId]: { ...prev[obraId], selected: !prev[obraId]?.selected },
        }))
        setError(null)
    }

    const setPorcentagem = (obraId, value) => {
        setSelections((prev) => ({
            ...prev,
            [obraId]: { ...prev[obraId], porcentagem: value },
        }))
        setError(null)
    }

    const validar = () => {
        if (!selecionadas.length) {
            setError('Selecione ao menos uma obra')
            return null
        }
        const fila = []
        for (const obra of selecionadas) {
            const pct = parseFloat(selections[obra.id]?.porcentagem)
            if (!selections[obra.id]?.porcentagem || isNaN(pct) || pct <= 0 || pct > 100) {
                setError(`Informe uma porcentagem válida (0–100) para ${obra.nome_obra || obra.codigo_obra}`)
                return null
            }
            fila.push({ obra_id: obra.id, porcentagem: String(pct), label: obra.nome_obra || obra.codigo_obra })
        }
        if (totalPorcentagem > 100) {
            setError('A soma das porcentagens selecionadas não pode ultrapassar 100%')
            return null
        }
        return fila
    }

    const handleSubmit = async () => {
        const fila = validar()
        if (!fila) return

        if (!multiplasObras) {
            setProcessing(true)
            const result = await onProcessQueue(fila, { onProgress: () => {} })
            setProcessing(false)
            if (result?.allOk) onClose()
            return
        }

        setProcessing(true)
        setQueueProgress({ done: 0, total: fila.length, current: fila[0]?.label || '' })
        const queueResults = []

        for (let i = 0; i < fila.length; i++) {
            const item = fila[i]
            setQueueProgress({ done: i, total: fila.length, current: item.label })
            const res = await onProcessQueue([item], { silent: true })
            queueResults.push({ ...item, ok: res?.allOk })
            setQueueProgress({ done: i + 1, total: fila.length, current: item.label })
            setResults([...queueResults])
        }

        setProcessing(false)
        if (queueResults.every((r) => r.ok)) {
            setTimeout(onClose, 800)
        }
    }

    const concluido = !processing && results.length > 0 && results.length === selecionadas.length

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={processing ? () => {} : onClose}>
                <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all">
                                <div className="relative bg-gray-900 px-6 pt-6 pb-12">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-2xl bg-brand-yellow flex items-center justify-center">
                                                <BuildingOffice2Icon className="w-6 h-6 text-gray-900" />
                                            </div>
                                            <div>
                                                <Dialog.Title as="h3" className="text-lg font-bold text-white">Vincular às Obras</Dialog.Title>
                                                <p className="text-sm text-gray-400 mt-0.5">
                                                    {investidorNome ? `${investidorNome} · ` : ''}defina a participação em cada obra
                                                </p>
                                            </div>
                                        </div>
                                        {!processing && (
                                            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10">
                                                <XMarkIcon className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="relative -mt-5 mx-6 z-10">
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 space-y-4 min-h-[200px]">
                                        {loading ? (
                                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                                <span className="h-10 w-10 rounded-full border-2 border-gray-200 border-t-brand-yellow animate-spin" />
                                                <p className="text-sm font-medium text-gray-600">Carregando obras do empreendimento...</p>
                                                <p className="text-xs text-gray-400">Aguarde ou clique em &quot;Vincular depois&quot;</p>
                                            </div>
                                        ) : obras.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                                <BuildingOffice2Icon className="w-10 h-10 text-gray-300 mb-2" />
                                                <p className="text-sm font-medium text-gray-600">Nenhuma obra cadastrada neste empreendimento</p>
                                                <p className="text-xs text-gray-400 mt-1">Cadastre obras antes de vincular o investidor.</p>
                                            </div>
                                        ) : (
                                        <>
                                        {multiplasObras && (
                                            <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-gray-50 border border-gray-100">
                                                <span className="text-xs font-semibold text-gray-500">Total selecionado</span>
                                                <span className={`text-sm font-bold ${totalPorcentagem > 100 ? 'text-red-600' : 'text-gray-900'}`}>
                                                    {totalPorcentagem.toFixed(2)}% / 100%
                                                </span>
                                            </div>
                                        )}

                                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                                            {obras.map((obra) => {
                                                const sel = selections[obra.id] || {}
                                                const result = results.find((r) => r.obra_id === obra.id)
                                                return (
                                                    <div
                                                        key={obra.id}
                                                        className={`rounded-xl border p-3 transition-all ${
                                                            sel.selected ? 'border-brand-yellow/50 bg-brand-yellow/5' : 'border-gray-100'
                                                        }`}
                                                    >
                                                        <button
                                                            type="button"
                                                            disabled={processing}
                                                            onClick={() => multiplasObras && toggleObra(obra.id)}
                                                            className={`w-full flex items-center gap-3 text-left ${multiplasObras ? 'cursor-pointer' : 'cursor-default'}`}
                                                        >
                                                            {multiplasObras && (
                                                                <span className={`pointer-events-none flex h-4 w-4 items-center justify-center rounded border ${sel.selected ? 'border-brand-yellow bg-brand-yellow' : 'border-gray-300 bg-white'}`}>
                                                                    {sel.selected && <span className="h-2 w-2 rounded-sm bg-gray-900" />}
                                                                </span>
                                                            )}
                                                            <div className="pointer-events-none flex-1 min-w-0">
                                                                <p className="text-sm font-semibold text-gray-900 truncate">{obra.nome_obra || '—'}</p>
                                                                <p className="text-xs text-gray-400">{obra.codigo_obra || obra.id}</p>
                                                            </div>
                                                            {result && (
                                                                result.ok
                                                                    ? <CheckCircleIcon className="pointer-events-none w-5 h-5 text-emerald-500 flex-shrink-0" />
                                                                    : <ExclamationCircleIcon className="pointer-events-none w-5 h-5 text-red-500 flex-shrink-0" />
                                                            )}
                                                        </button>
                                                        {(sel.selected || !multiplasObras) && (
                                                            <div className={`mt-2 ${multiplasObras ? 'pl-7' : ''}`}>
                                                                <label className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase mb-1">
                                                                    <ChartPieIcon className="w-3 h-3" /> Participação (%)
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    min="0.01"
                                                                    max="100"
                                                                    step="0.01"
                                                                    value={sel.porcentagem || ''}
                                                                    onChange={(e) => setPorcentagem(obra.id, e.target.value)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    disabled={processing}
                                                                    placeholder="Ex.: 50"
                                                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-yellow disabled:bg-gray-50"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>

                                        {processing && multiplasObras && (
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs font-semibold text-gray-500">
                                                    <span>Processando fila...</span>
                                                    <span>{queueProgress.done}/{queueProgress.total}</span>
                                                </div>
                                                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                                                    <div
                                                        className="h-full bg-brand-yellow transition-all duration-300"
                                                        style={{ width: `${queueProgress.total ? (queueProgress.done / queueProgress.total) * 100 : 0}%` }}
                                                    />
                                                </div>
                                                {queueProgress.current && (
                                                    <p className="text-xs text-gray-400 truncate">Última: {queueProgress.current}</p>
                                                )}
                                            </div>
                                        )}

                                        {error && <p className="text-xs text-red-500">⚠ {error}</p>}
                                        {concluido && !results.every((r) => r.ok) && (
                                            <p className="text-xs text-amber-600">Alguns vínculos falharam. Verifique os itens marcados em vermelho.</p>
                                        )}
                                        </>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3 px-6 pt-4 pb-6">
                                    {!processing && (
                                        <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200">
                                            {concluido ? 'Fechar' : 'Vincular depois'}
                                        </button>
                                    )}
                                    {!concluido && !loading && obras.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            disabled={processing}
                                            className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-900 bg-brand-yellow hover:bg-brand-yellow-dark shadow-md disabled:opacity-50"
                                        >
                                            {processing ? 'Processando...' : multiplasObras ? 'Vincular nas obras' : 'Vincular à obra'}
                                        </button>
                                    )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalVincularObrasInvestidor
